import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { deleteAccountApi } from '../api';

const inputClass =
  'w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink/35 focus:outline-none focus:ring-2 focus:ring-brand/60 focus:border-brand transition';

const labelClass = 'block mb-1.5 text-sm font-medium text-ink/80';

export default function DeleteAccount() {
  const [step, setStep] = useState('identify'); // identify | confirm | done
  const [coinsfluId, setCoinsfluId] = useState('');
  const [pin, setPin] = useState('');
  const [otp, setOtp] = useState('');
  const [maskedMobile, setMaskedMobile] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = 'Coinsflu — Delete Your Account';
    return () => {
      document.title = 'Coinsflu';
    };
  }, []);

  const resetToIdentify = (message = '') => {
    setStep('identify');
    setOtp('');
    setError('');
    setNotice(message);
    setMaskedMobile('');
  };

  const handleIdentify = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    const result = await deleteAccountApi.init(coinsfluId.trim(), pin);
    setLoading(false);

    if (result.success) {
      setMaskedMobile(result.data?.masked_mobile || '');
      setOtp('');
      setStep('confirm');
    } else {
      setError(result.message || 'Verification failed. Please try again.');
    }
  };

  const handleDelete = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    const result = await deleteAccountApi.confirm(coinsfluId.trim(), pin, otp);
    setLoading(false);

    if (result.success) {
      setStep('done');
      setPin('');
      setOtp('');
    } else if (result.status === 409) {
      // Legitimate pending financial obligation — the account is NOT deleted.
      setNotice('');
      setError(result.message || 'Your account cannot be deleted right now.');
    } else {
      setError(result.message || 'Could not delete account. Please try again.');
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setNotice('');
    setLoading(true);
    const result = await deleteAccountApi.init(coinsfluId.trim(), pin);
    setLoading(false);

    if (result.success) {
      setMaskedMobile(result.data?.masked_mobile || '');
      setNotice('A new OTP has been sent to your registered mobile number.');
    } else {
      setError(result.message || 'Could not resend OTP. Please try again.');
    }
  };

  return (
    <div
      data-testid="delete-account-page"
      className="min-h-screen bg-white px-5 md:px-10 pt-28 md:pt-32 pb-20"
    >
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="delete-account-back-link"
          className="inline-flex items-center gap-2 text-ink/60 hover:text-brand transition-colors text-sm"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>

        <span className="mt-8 block font-pixel text-[11px] tracking-[0.25em] uppercase text-brand">
          Account
        </span>
        <h1 className="mt-3 text-4xl md:text-6xl font-semibold tracking-tightest leading-[1.05]">
          Delete Your Coinsflu Account
        </h1>
        <p className="mt-5 text-[15px] md:text-base leading-[1.8] text-ink/75">
          Coinsflu is developed and operated by{' '}
          <strong>King's Wake Digital Private Limited</strong>. This page lets
          you permanently delete your Coinsflu account from the web — you do
          not need to install or open the Coinsflu app.
        </p>

        <div className="mt-8 rounded-3xl bg-surface-muted border border-black/5 p-7 md:p-8">
          <h2 className="text-lg font-semibold text-ink">
            What deleting your account means
          </h2>
          <p className="mt-3 text-[15px] leading-[1.8] text-ink/75">
            Deleting your Coinsflu account will permanently remove your account
            and eligible associated personal data. Certain transaction or
            security records may be retained where required by applicable law
            or legitimate financial and security obligations.
          </p>
          <ul className="mt-4 space-y-2.5 text-[15px] leading-[1.8] text-ink/75 pl-1">
            {[
              'Your Coinsflu profile (name, Coinsflu ID, mobile number, profile image) is anonymized and can no longer be used to sign in.',
              'You lose access to your wallet balance, rewards, Gift Program participation, followers, following, messages and other account-linked content.',
              'Any Jantaquid account linkage and device sessions are released and sign-in tokens are revoked.',
              'Financial records such as wallet transactions and completed withdrawals are retained only as required for legal, accounting, fraud-prevention and security obligations, in line with our Privacy Policy.',
              'If you have a withdrawal still being reviewed, deletion must wait until that review is complete.',
            ].map((point) => (
              <li
                key={point}
                className="relative pl-6 before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-2 before:h-2 before:bg-brand before:rounded-sm"
              >
                {point}
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[15px] leading-[1.8] text-ink/75">
            You can also delete your account inside the app under{' '}
            <strong>Settings → Delete Account</strong>. Both use the same
            deletion system. Need help? Contact{' '}
            <a
              href="mailto:team@kingswake.co.in"
              className="text-brand underline underline-offset-2"
            >
              team@kingswake.co.in
            </a>
            .
          </p>
        </div>

        {step === 'identify' && (
          <form
            onSubmit={handleIdentify}
            data-testid="identify-form"
            className="mt-8 rounded-3xl border border-black/5 p-7 md:p-8"
          >
            <h2 className="text-lg font-semibold text-ink">
              Step 1 — Verify your account
            </h2>
            <p className="mt-2 text-[15px] leading-[1.8] text-ink/70">
              Enter the Coinsflu ID shown on your profile and the 4-digit PIN
              you use to sign in to the Coinsflu app. We will then send a
              one-time verification code (OTP) to your registered mobile
              number.
            </p>

            <div className="mt-6">
              <label htmlFor="coinsflu-id" className={labelClass}>
                Coinsflu ID
              </label>
              <input
                id="coinsflu-id"
                data-testid="coinsflu-id-input"
                className={inputClass}
                type="text"
                autoComplete="username"
                placeholder="@YourCoinsfluID"
                value={coinsfluId}
                onChange={(e) => setCoinsfluId(e.target.value)}
                required
                maxLength={64}
              />
            </div>

            <div className="mt-5">
              <label htmlFor="coinsflu-pin" className={labelClass}>
                4-digit PIN
              </label>
              <input
                id="coinsflu-pin"
                data-testid="pin-input"
                className={inputClass}
                type="password"
                inputMode="numeric"
                autoComplete="off"
                placeholder="••••"
                value={pin}
                onChange={(e) =>
                  setPin(e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                required
                minLength={4}
                maxLength={4}
              />
            </div>

            {notice && (
              <p
                data-testid="delete-account-notice"
                role="status"
                className="mt-4 text-sm text-ink/70"
              >
                {notice}
              </p>
            )}
            {error && (
              <p
                data-testid="delete-account-error"
                role="alert"
                className="mt-4 flex items-start gap-2 text-sm text-brand"
              >
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="continue-button"
              disabled={loading || coinsfluId.trim() === '' || pin.length !== 4}
              className="btn-primary mt-7 inline-flex items-center justify-center rounded-full px-7 py-3 text-[15px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'confirm' && (
          <form
            onSubmit={handleDelete}
            data-testid="confirm-form"
            className="mt-8 rounded-3xl border border-black/5 p-7 md:p-8"
          >
            <h2 className="text-lg font-semibold text-ink">
              Step 2 — Confirm permanent deletion
            </h2>
            <p className="mt-2 flex items-start gap-2 text-[15px] leading-[1.8] text-ink/70">
              <ShieldCheck size={18} className="mt-1 shrink-0 text-brand" />
              <span>
                We sent a 6-digit verification code to your registered mobile
                number{' '}
                <strong data-testid="masked-mobile">{maskedMobile}</strong>.
                Enter it below and press{' '}
                <strong>Delete My Account</strong> to permanently delete your
                account. This cannot be undone.
              </span>
            </p>

            <div className="mt-6">
              <label htmlFor="deletion-otp" className={labelClass}>
                6-digit OTP
              </label>
              <input
                id="deletion-otp"
                data-testid="otp-input"
                className={inputClass}
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="••••••"
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                required
                minLength={6}
                maxLength={6}
              />
            </div>

            <button
              type="button"
              data-testid="resend-otp-button"
              onClick={handleResendOtp}
              disabled={loading}
              className="mt-3 text-sm text-brand underline underline-offset-2 disabled:opacity-50"
            >
              Resend OTP
            </button>

            {notice && (
              <p
                data-testid="delete-account-notice"
                role="status"
                className="mt-4 text-sm text-ink/70"
              >
                {notice}
              </p>
            )}
            {error && (
              <p
                data-testid="delete-account-error"
                role="alert"
                className="mt-4 flex items-start gap-2 text-sm text-brand"
              >
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                {error}
              </p>
            )}

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                data-testid="confirm-delete-button"
                disabled={loading || otp.length !== 6}
                className="btn-primary inline-flex items-center justify-center rounded-full px-7 py-3 text-[15px] font-semibold text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting…' : 'Delete My Account'}
              </button>
              <button
                type="button"
                data-testid="cancel-button"
                onClick={() => resetToIdentify()}
                disabled={loading}
                className="inline-flex items-center justify-center rounded-full px-7 py-3 text-[15px] font-semibold text-ink border border-black/15 hover:border-ink/40 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
            <p className="mt-3 text-sm text-ink/55">
              Pressing Cancel keeps your account exactly as it is — nothing is
              deleted.
            </p>
          </form>
        )}

        {step === 'done' && (
          <div
            data-testid="delete-success"
            className="mt-8 rounded-3xl border border-black/5 bg-surface-muted p-7 md:p-8"
          >
            <h2 className="flex items-center gap-2 text-lg font-semibold text-ink">
              <CheckCircle2 size={20} className="text-brand" />
              Your Coinsflu account has been deleted successfully.
            </h2>
            <p className="mt-3 text-[15px] leading-[1.8] text-ink/75">
              Your account access has been removed. Associated personal data
              has been deleted or anonymized according to our{' '}
              <Link
                to="/privacy"
                data-testid="success-privacy-link"
                className="text-brand underline underline-offset-2"
              >
                Privacy Policy
              </Link>
              , except records that must be retained for legitimate legal or
              financial obligations.
            </p>
            <Link
              to="/"
              data-testid="success-home-link"
              className="btn-primary mt-6 inline-flex items-center justify-center rounded-full px-7 py-3 text-[15px] font-semibold text-white"
            >
              Back to home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
